import React, { useEffect, useState } from 'react';
import Sidebar from '../components/layout/sidebar';
import Topbar from '../components/layout/Topbar';
import { supabase } from '../supabase';
import './feedback.css';

const Feedback = () => {
  const [feedback, setFeedback] = useState([]);
  const [search, setSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (localStorage.getItem('isAdminLoggedIn') !== 'true') {
      window.location.href = '/';
      return;
    }

    fetchEvents();
    fetchFeedback();
  }, []);

  useEffect(() => {
    fetchFeedback();
  }, [search, ratingFilter, eventFilter]);

  const fetchEvents = async () => {
    const { data } = await supabase.from('events').select('id, title_en');
    setEvents(data || []);
  };

  const fetchFeedback = async () => {
    let query = supabase
      .from('feedback')
      .select(`
        *,
        users (
          name,
          email
        ),
        events (
          title_en
        )
      `)
      .order('created_at', { ascending: false });

    if (ratingFilter) {
      query = query.eq('rating', ratingFilter);
    }

    if (eventFilter) {
      query = query.eq('event_id', eventFilter);
    }

    const { data, error } = await query;

    if (error) {
      alert(error.message);
      return;
    }

    let filteredData = data || [];

    if (search) {
      filteredData = filteredData.filter((item) => {
        const userName = item.users?.name || '';
        const userEmail = item.users?.email || '';
        const eventTitle = item.events?.title_en || '';
        const comment = item.comment || '';

        return `${userName} ${userEmail} ${eventTitle} ${comment}`
          .toLowerCase()
          .includes(search.toLowerCase());
      });
    }

    setFeedback(filteredData);
  };

  const deleteFeedback = async (id) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this feedback?'
    );

    if (!confirmDelete) return;

    const { error } = await supabase.from('feedback').delete().eq('id', id);

    if (error) {
      alert(error.message);
      return;
    }

    fetchFeedback();
  };

  const totalReviews = feedback.length;

  const averageRating =
    totalReviews > 0
      ? (
          feedback.reduce((sum, item) => sum + Number(item.rating || 0), 0) /
          totalReviews
        ).toFixed(1)
      : '0.0';

  const ratingCounts = {
    5: feedback.filter((item) => item.rating === 5).length,
    4: feedback.filter((item) => item.rating === 4).length,
    3: feedback.filter((item) => item.rating === 3).length,
    2: feedback.filter((item) => item.rating === 2).length,
    1: feedback.filter((item) => item.rating === 1).length,
  };

  const lowRatings = ratingCounts[1] + ratingCounts[2];

  const renderStars = (rating) => {
    const fullStars = '★'.repeat(Number(rating || 0));
    const emptyStars = '☆'.repeat(5 - Number(rating || 0));
    return fullStars + emptyStars;
  };

  return (
    <div className="dashboard-page">
      <Sidebar active="Feedback & Reviews" />

      <main className="dashboard-main">
        <Topbar />

        <section className="feedback-content">
          <div className="feedback-header">
            <h1>Feedback & Reviews</h1>
            <p>Monitor and manage customer feedback</p>
          </div>

          <div className="feedback-summary-grid">
            <div className="feedback-summary-card average">
              <div className="avg-row">
                <div className="avg-icon">☆</div>
                <div>
                  <p>Average Rating</p>
                  <h2>{averageRating}</h2>
                </div>
              </div>

              <div className="big-stars">{renderStars(Math.round(averageRating))}</div>
            </div>

            <div className="feedback-summary-card">
              <p>Total Reviews</p>
              <h2>{totalReviews}</h2>

              <div className="rating-bars">
                {[5, 4, 3, 2, 1].map((rating) => (
                  <div className="rating-bar-row" key={rating}>
                    <span>{rating}★</span>
                    <div className="rating-bar-track">
                      <div
                        className="rating-bar-fill"
                        style={{
                          width:
                            totalReviews > 0
                              ? `${(ratingCounts[rating] / totalReviews) * 100}%`
                              : '0%',
                        }}
                      ></div>
                    </div>
                    <strong>{ratingCounts[rating]}</strong>
                  </div>
                ))}
              </div>
            </div>

            <div className="feedback-summary-card">
              <p>Rating Breakdown</p>

              <div className="breakdown-list">
                <div>
                  <span>5 Stars</span>
                  <strong className="green">{ratingCounts[5]}</strong>
                </div>
                <div>
                  <span>4 Stars</span>
                  <strong className="green">{ratingCounts[4]}</strong>
                </div>
                <div>
                  <span>3 Stars</span>
                  <strong className="orange">{ratingCounts[3]}</strong>
                </div>
                <div>
                  <span>1-2 Stars</span>
                  <strong className="red">{lowRatings}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="feedback-filter-card">
            <div className="feedback-search">
              <span>⌕</span>
              <input
                placeholder="Search reviews..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select value={ratingFilter} onChange={(e) => setRatingFilter(e.target.value)}>
              <option value="">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>

            <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}>
              <option value="">All Events</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title_en}
                </option>
              ))}
            </select>
          </div>

          <div className="reviews-grid">
            {feedback.map((review) => (
              <div className="review-card" key={review.id}>
                <button
                  className="delete-review-btn"
                  onClick={() => deleteFeedback(review.id)}
                >
                  🗑
                </button>

                <div className="review-user">
                  <div className="review-avatar">♙</div>

                  <div>
                    <h3>{review.users?.name || 'Unknown User'}</h3>
                    <p>{review.users?.email || 'No email'}</p>
                  </div>
                </div>

                <div className="review-stars">
                  {renderStars(review.rating)} <span>({review.rating}/5)</span>
                </div>

                <h4>{review.events?.title_en || 'Unknown Event'}</h4>

                <p className="review-comment">{review.comment}</p>

                <p className="review-date">
                  ▣ {new Date(review.created_at).toISOString().slice(0, 10)}
                </p>
              </div>
            ))}
          </div>

          {feedback.length === 0 && (
            <div className="empty-feedback">No reviews found.</div>
          )}
        </section>

        <footer className="dashboard-footer">
          <p>© 2026 Spine Literary Events Platform. All rights reserved.</p>
          <p>Admin Dashboard v1.0</p>
        </footer>
      </main>
    </div>
  );
};

export default Feedback;